import React, { useState } from 'react';
import { Typography, Box, Avatar, Chip } from '@mui/material';
import { Circle as CircleIcon, Person as PersonIcon } from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import { ROLE_LABELS, WHITE_TEXT_ROLES, getRoleColors } from 'shared/constants/roleConstants';

const initialsFromName = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

export default function RoleAssignedNotificationCard({ notification, onMarkRead }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const payload = notification.payload || {};
  const [imgBroken, setImgBroken] = useState(false);

  // The assigner may be unknown; render anyway so the badge never counts an invisible card.
  if (!payload.consortium_id) return null;
  const assignerAvatarSrc = payload.assigner_avatar || payload.assigner_avatar_url || null;
  const showInitials = !assignerAvatarSrc || imgBroken;

  const ROLE_COLOR = getRoleColors(theme);

  const handleClick = () => {
    if (!notification.is_read && onMarkRead) onMarkRead();
    if (payload.proposal_id) {
      const section = payload.document_type === 'publication' ? 'publications' : 'proposals';
      navigate(`/consortium/${payload.consortium_id}/${section}/${payload.proposal_id}/`);
    } else {
      navigate(`/consortium/${payload.consortium_id}/`);
    }
  };

  const roleKey = payload.role || '';
  const label = payload.role_label || ROLE_LABELS[roleKey] || roleKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Role';
  const chipBgColor = ROLE_COLOR[roleKey] ?? theme.palette.primary.xulight;
  const chipTextColor = WHITE_TEXT_ROLES.includes(roleKey) ? '#fff' : 'inherit';

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        py: 1, px: 2,
        borderBottom: '1px solid', borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.selected' },
      }}
    >
      <Avatar
        src={showInitials ? undefined : assignerAvatarSrc}
        onError={() => setImgBroken(true)}
        sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.avatar, color: 'white', mt: 0.25, fontSize: 13 }}
      >
        {payload.assigner_name ? initialsFromName(payload.assigner_name) : <PersonIcon sx={{ fontSize: 18 }} />}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={600}>You've been assigned as</Typography>
          <Chip
            label={label}
            size="small"
            sx={{ height: 18, fontSize: '0.7rem', bgcolor: chipBgColor, color: chipTextColor }}
          />
          {payload.consortium_name && (
            <Typography variant="body2" color="text.secondary">
              in <Box component="span" fontWeight={500} color="text.primary">{payload.consortium_name}</Box>
            </Typography>
          )}
          {payload.assigner_name && (
            <Typography variant="body2" color="text.secondary">
              by <Box component="span" fontWeight={500} color="text.primary">{payload.assigner_name}</Box>
            </Typography>
          )}
      

        {payload.wp_title && (

<Chip
label=  {payload.wp_title.slice(0, 20) + (payload.wp_title.length > 20 ? '...' : '')}
size="small"
sx={{ height: 18, fontSize: '0.95rem', color:"text.secondary" }}
/>

        )}

        {(payload.proposal_acronym || payload.proposal_title) && (

<Chip
label={payload.proposal_acronym || (payload.proposal_title.length > 30 ? `${payload.proposal_title.slice(0, 30)}…` : payload.proposal_title)}
size="small"
sx={{ height: 18, fontSize: '0.95rem', color:"text.secondary" }}
/>


        )}

</Box>

      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
      </Box>
    </Box>
  );
}
